/**
 * SyncPill — live "data is being refreshed" indicator on every page.
 *
 * Why this exists: imports run invisibly once an account is past onboarding.
 * Jeremy watched numbers shift mid-session with no explanation ("what sync
 * banner?" — there wasn't one), and a dead import once displayed "running"
 * for 25 minutes. The pill makes background data work — and its failures —
 * visible everywhere, without being a dashboard fixture when idle.
 *
 * States:
 *   hidden  — nothing running, nothing recently failed (the normal state)
 *   active  — "Refreshing: importing store orders · 58%" with a live spinner
 *   failed  — red: the last refresh died (the watchdog auto-retries; if a
 *             retry is already running, active wins — the retry IS the news)
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "./api-context.js";
import { useIntegrationCatalog } from "./integration-catalog.js";

interface SyncJob {
  id: string;
  jobType: string;
  status: "queued" | "running";
  progressCurrent: number | null;
  progressTotal: number | null;
}

interface ActiveResponse {
  jobs: SyncJob[];
  recentFailures: {
    count: number;
    latest: { jobType: string; errorMessage: string | null; finishedAt: string } | null;
  };
}

export function SyncPill(): React.ReactElement | null {
  const api = useApiClient();
  const { data } = useQuery({
    queryKey: ["sync-pill"],
    queryFn: () => api.get<ActiveResponse>("/sync-jobs/active"),
    // 12s: fast enough to feel live, light enough to leave running forever.
    refetchInterval: 12_000,
    refetchOnWindowFocus: true,
  });
  // Plain-words job labels come from the registry catalog (one source of truth).
  const { data: catalog } = useIntegrationCatalog();
  const jobLabels = catalog?.jobLabels ?? {};

  const jobs = data?.jobs ?? [];
  // Prefer the long import for the label/progress; anything else still shows.
  const job = jobs.find((j) => j.status === "running") ?? jobs[0];

  // ACTIVE — say plainly WHAT is happening right now, in the founder's words
  // (the label is already a present-progressive phrase like "importing your
  // orders" / "rebuilding your profit margins"), with progress when we have it.
  // Founder 2026-07-11: "if something's happening in the background, they should
  // know what's happening" — and a positive "working on it" beats a scary error.
  if (job) {
    const label = jobLabels[job.jobType] ?? (job.jobType === "verify_full" ? "checking your numbers" : "updating your numbers");
    const pct =
      job.progressTotal && job.progressTotal > 0 && job.progressCurrent !== null
        ? Math.min(100, Math.round((job.progressCurrent / job.progressTotal) * 100))
        : null;
    return (
      <span style={{ ...pillStyle, color: "var(--body)" }} title="Working in the background — some numbers may keep moving until this finishes.">
        <span className="dd-spinner" style={{ width: 10, height: 10, borderWidth: 1.5, flexShrink: 0 }} />
        <span>
          {cap(label)}
          {pct !== null && <b className="dd-num" style={{ marginLeft: 4 }}>{pct}%</b>}
          {pct === null && <span>…</span>}
        </span>
      </span>
    );
  }

  // NEEDS ATTENTION — only for a genuine, unresolved failure with nothing
  // currently running. Name the specific task in plain words (so it can't
  // contradict "last import 4h ago" — a stalled margin rebuild is NOT "your data
  // failed"), and don't claim an auto-retry we can't promise.
  const fail = data?.recentFailures;
  if (fail && fail.count > 0 && fail.latest) {
    const label = jobLabels[fail.latest.jobType] ?? (fail.latest.jobType === "verify_full" ? "the full number check" : "one background task");
    return (
      <span
        style={{ ...pillStyle, color: "var(--body-faint)", borderColor: "color-mix(in srgb, var(--warn, #b5820a) 40%, transparent)" }}
        title="A background task didn't finish last time. Your imported data is unaffected; this reruns the next time your data updates. If it keeps happening, tell us with Submit a bug."
      >
        <span style={{ color: "var(--warn, #b5820a)", fontSize: 11, lineHeight: 1 }}>●</span>
        <span>Still finishing {label} — reruns on your next update</span>
      </span>
    );
  }

  return null;
}

/** Sentence-case the first letter of a plain-language label for pill display. */
function cap(s: string): string {
  return s.length ? s[0]!.toUpperCase() + s.slice(1) : s;
}

const pillStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  fontSize: 12,
  padding: "4px 10px",
  borderRadius: 999,
  border: "1px solid var(--hair)",
  background: "var(--card)",
  whiteSpace: "nowrap",
};
